/*
 * The theme's vocabulary and its two palettes. The colours are written on
 * <html> as --theme-* custom properties by applyThemeToDom.
 */
export type ThemeMode = 'light' | 'dark' | 'auto';
export type ThemeVariant = 'default' | 'compact' | 'comfortable';

export interface ThemeColors {
  primary: string;
  primaryLight: string;
  primaryDark: string;

  bgPrimary: string;
  bgSecondary: string;
  bgTertiary: string;
  bgElevated: string;
  bgOverlay: string;

  textPrimary: string;
  textSecondary: string;
  textTertiary: string;
  textMuted: string;
  textInverse: string;

  borderPrimary: string;
  borderSecondary: string;
  borderFocus: string;
  borderError: string;

  interactiveHover: string;
  interactiveActive: string;
  interactiveDisabled: string;

  success: string;
  error: string;
  warning: string;
  info: string;

  shadowSm: string;
  shadowMd: string;
  shadowLg: string;
  shadowXl: string;
}

export interface ThemeConfig {
  mode: ThemeMode;
  variant: ThemeVariant;
  colors: ThemeColors;
  animations: boolean;
  reducedMotion: boolean;
}

export const lightColors: ThemeColors = {
  primary: '#215EA1',
  primaryLight: '#3a7bc8',
  primaryDark: '#1a4d85',

  bgPrimary: '#ffffff',
  bgSecondary: '#fafbfc',
  bgTertiary: '#f2f4f7',
  bgElevated: '#ffffff',
  bgOverlay: 'rgba(0, 0, 0, 0.4)',

  textPrimary: '#111827',
  textSecondary: '#374151',
  textTertiary: '#6b7280',
  textMuted: '#9ca3af',
  textInverse: '#ffffff',

  borderPrimary: '#e5e7eb',
  borderSecondary: '#d1d5db',
  borderFocus: '#215EA1',
  borderError: '#ef4444',

  interactiveHover: '#f9fafb',
  interactiveActive: '#f3f4f6',
  interactiveDisabled: '#e5e7eb',

  success: '#10b981',
  error: '#ef4444',
  warning: '#f59e0b',
  info: '#3b82f6',

  shadowSm: '0 1px 2px rgba(0, 0, 0, 0.05)',
  shadowMd: '0 4px 6px rgba(0, 0, 0, 0.07)',
  shadowLg: '0 10px 15px rgba(0, 0, 0, 0.1)',
  shadowXl: '0 25px 50px rgba(0, 0, 0, 0.15)',
};

/**
 * One dark scale for the whole interface, the same greys as the --neutral-*
 * dark values in styles/enhanced-dark-theme.css and the navigation bars:
 * canvas #0d1117, surface #161b22, raised #1c2128, muted #21262d, border #30363d.
 */
export const darkColors: ThemeColors = {
  primary: '#58a6ff',
  primaryLight: '#79c0ff',
  primaryDark: '#388bfd',

  bgPrimary: '#161b22',
  bgSecondary: '#0d1117',
  bgTertiary: '#21262d',
  bgElevated: '#1c2128',
  bgOverlay: 'rgba(1, 4, 9, 0.8)',

  textPrimary: '#e6edf3',
  textSecondary: '#b1bac4',
  textTertiary: '#8b949e',
  textMuted: '#6e7681',
  textInverse: '#0d1117',

  borderPrimary: '#30363d',
  borderSecondary: '#484f58',
  borderFocus: '#58a6ff',
  borderError: '#f85149',

  interactiveHover: '#21262d',
  interactiveActive: '#30363d',
  interactiveDisabled: '#21262d',

  success: '#3fb950',
  error: '#f85149',
  warning: '#d29922',
  info: '#58a6ff',

  shadowSm: '0 1px 2px rgba(0, 0, 0, 0.3)',
  shadowMd: '0 4px 6px rgba(0, 0, 0, 0.4)',
  shadowLg: '0 10px 15px rgba(0, 0, 0, 0.5)',
  shadowXl: '0 25px 50px rgba(0, 0, 0, 0.7)',
};

export const paletteFor = (dark: boolean): ThemeColors => (dark ? darkColors : lightColors);

export const defaultTheme: ThemeConfig = {
  mode: 'auto',
  variant: 'default',
  colors: lightColors,
  animations: true,
  reducedMotion: false,
};
